<template>
  <div class="tabs">
    <button
      :class="[
        'tabs__nav-button',
        'tabs__nav-button_prev',
        { 'tabs__nav-button_disabled': !haveScrollLeft }
      ]"
      @click="selectPrev"
    >
      <ChevronIcon class="tabs__nav-button-icon" />
    </button>
    <div class="tabs__slider-wrapper">
      <Flickity
        ref="flickity"
        :key="`tabs-slider-${_uid}`"
        :options="sliderOptions"
        :class="[
          'tabs__slider',
          {'tabs__slider_have-scroll-right': showRightGradient},
          {'tabs__slider_have-scroll-left': showLeftGradient}
        ]"
      >
        <button
          v-for="tab in tabs"
          :key="`tab-${tab.value}`"
          :class="[
            'tabs__tab',
            {'tabs__tab_active': activeTabValue === tab.value}
          ]"
          @click="selectTab(tab.value)"
        >
          {{ tab.text }}
        </button>
      </Flickity>
    </div>
    <button
      :class="[
        'tabs__nav-button',
        'tabs__nav-button_next',
        { 'tabs__nav-button_disabled': !haveScrollRight }
      ]"
      @click="selectNext"
    >
      <ChevronIcon class="tabs__nav-button-icon" />
    </button>
  </div>
</template>

<script>
  import ChevronIcon from '@/assets/img/icons/chevron.svg?inline'
  import Flickity from 'vue-flickity'
  import { get } from 'lodash'
  import { mapGetters } from 'vuex'

  Flickity.beforeDestroy = undefined // stop breaking when transition

  export default {
    name: 'BaseTabs',
    components: {
      ChevronIcon,
      Flickity
    },
    props: {
      tabs: {
        type: Array,
        required: true,
        validator (tabs) {
          return tabs.every(tab => {
            return 'value' in tab &&
              'text' in tab
          })
        }
      }
    },
    data () {
      return {
        sliderOptions: {
          prevNextButtons: false,
          pageDots: false,
          freeScroll: true,
          contain: true,
          cellAlign: 'left'
        },
        activeTabValue: null
      }
    },
    computed: {
      ...mapGetters(['isMobile']),
      activeTabIndex () {
        return this.tabs.indexOf(this.tabs.find(tab => tab.value === this.activeTabValue))
      },
      haveScrollRight () {
        const nextTabIndex = this.activeTabIndex + 1
        return nextTabIndex < this.tabs.length
      },
      haveScrollLeft () {
        const prevTabIndex = this.activeTabIndex - 1
        return prevTabIndex >= 0
      },
      showRightGradient () {
        return this.haveScrollRight && (this.isMobile || this.tabs.length > 2)
      },
      showLeftGradient () {
        return this.haveScrollLeft && (this.isMobile || this.tabs.length > 2)
      }
    },
    mounted () {
      this.selectTab(this.tabs[0].value)
    },
    methods: {
      selectTab (tabValue) {
        this.activeTabValue = tabValue
        this.$emit('tabChange', this.tabs.find(tab => tab.value === tabValue))
        if (this.$refs.flickity) {
          this.$refs.flickity.select(this.activeTabIndex)
        }
      },
      selectPrev () {
        const prevTabValue = get(this.tabs[this.activeTabIndex - 1], 'value')
        if (!this.haveScrollLeft) return
        this.selectTab(prevTabValue)
      },
      selectNext () {
        const nextTabValue = get(this.tabs[this.activeTabIndex + 1], 'value')
        if (!this.haveScrollRight) return
        this.selectTab(nextTabValue)
      }
    }
  }
</script>

<style lang="scss">
  .tabs {
    position: relative;

    &__nav-button {
      position: absolute;
      top: 53%;
      width: 17px;
      padding: 0;
      cursor: pointer;
      background: none;
      border: 0;
      outline: none;
      opacity: 0.7;
      transition: opacity 0.3s;
      transform: translateY(-50%);

      &-icon {
        width: 100%;
        height: 100%;
        stroke: $c-gray-dark;
      }

      @include hover {
        opacity: 1;
      }

      &_prev {
        left: -25px;

        @include tablet {
          left: -6px;
        }
      }

      &_next {
        right: -25px;
        transform: translateY(-50%) rotate(180deg);

        @include tablet {
          right: -6px;
        }
      }

      &_disabled {
        cursor: default;
        opacity: 0.3 !important;
      }
    }

    &__slider {
      position: relative;
      outline: none;

      @include tablet {
        margin: 0 20px;
      }

      &::after,
      &::before {
        position: absolute;
        top: 0;
        z-index: 4;
        width: 15px;
        height: 100%;
        content: "";
        opacity: 0;
        transition: opacity 0.3s;
      }

      &::after {
        right: 0;
        background: linear-gradient(to right, rgba(255, 255, 255, 0), $c-gray-light);
      }

      &::before {
        left: 0;
        background: linear-gradient(to left, rgba(255, 255, 255, 0), $c-gray-light);
      }

      &_have-scroll-right {
        &::after {
          opacity: 1;
        }
      }

      &_have-scroll-left {
        &::before {
          opacity: 1;
        }
      }
    }

    &__tab {
      padding: 1.15em 1.45em;
      margin-right: 20px;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 15px;
      line-height: 1em;
      color: $c-gray-dark;
      white-space: nowrap;
      cursor: pointer;
      background-color: $c-white;
      border: none;
      border-radius: 1.7em;
      outline: none;
      transition: color 0.3s;

      &:last-child {
        margin-right: 0;
      }

      &_active {
        color: $c-active;
      }

      @include hover {
        color: $c-active;
      }

      &:focus {
        color: $c-active-light;
      }

      @include tablet {
        margin-right: 12px;
      }
    }
  }
</style>
